import {
  isDesktopPath,
  isDesktopSettingsPath,
} from "@/components/sidebar/useActiveApp";
import { codesmithDesktopBridge } from "@/lib/codesmithSurface";
import {
  normalizeDashboardPathname,
  splitInstanceBasePath,
} from "@/lib/dashboard-routes";

/**
 * In the desktop app, opens a same-origin destination outside [code]smith
 * (usage, monitors, `/settings/integrations`, ...) in the system browser and
 * returns true so the caller stays put. Electron's `will-navigate` never sees
 * client-side navigations, so link clicks and router pushes both check here.
 */
export function openOutsideDesktop(url: string): boolean {
  const desktop = codesmithDesktopBridge();
  if (!desktop) return false;
  const destination = new URL(url, window.location.href);
  if (destination.origin !== window.location.origin) return false;
  // The org-less `/settings/codesmith/...` stubs normalize to the
  // `/settings/[[...section]]` catch-all, so read the section off the path
  // before route matching.
  if (
    isDesktopPath(normalizeDashboardPathname(destination.pathname)) ||
    isDesktopSettingsPath(splitInstanceBasePath(destination.pathname).pathname)
  ) {
    return false;
  }
  desktop.openExternal(destination.href);
  return true;
}

/**
 * Signs out through the backend logout URL. The desktop shell hands backend
 * navigations to the system browser, whose cookies are not the app's, so
 * there the request runs in the app's own cookie jar and the window reloads
 * into the signed-out view.
 */
export function navigateToLogout(url: string): void {
  if (!codesmithDesktopBridge()) {
    window.location.href = url;
    return;
  }
  void fetch(url, { mode: "no-cors", credentials: "include" })
    .catch(() => undefined)
    .finally(() => window.location.reload());
}
